import type { RefObject } from 'react';
import { useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { KanbanWorkOrderView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';

const ESTIMATED_CARD_BASE = 120;
const ESTIMATED_TICKET_ROW = 52;
const OVERSCAN = 6;

export function estimateKanbanCardHeight(
  workOrder: KanbanWorkOrderView,
): number {
  return (
    ESTIMATED_CARD_BASE +
    Math.max(0, workOrder.tickets.length - 1) * ESTIMATED_TICKET_ROW
  );
}

export function useKanbanColumnVirtualization(args: {
  workOrders: KanbanWorkOrderView[];
  scrollParentRef: RefObject<HTMLDivElement | null>;
  onReachEnd?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}) {
  const {
    workOrders,
    scrollParentRef,
    onReachEnd,
    hasNextPage = false,
    isFetchingNextPage = false,
  } = args;

  const virtualizer = useVirtualizer({
    count: workOrders.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: (index) => {
      const wo = workOrders[index];
      return wo ? estimateKanbanCardHeight(wo) : ESTIMATED_CARD_BASE;
    },
    overscan: OVERSCAN,
    getItemKey: (index) => workOrders[index]?.id ?? index,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!onReachEnd || !hasNextPage || isFetchingNextPage) return;
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= workOrders.length - 4) {
      onReachEnd();
    }
  }, [
    virtualItems,
    workOrders.length,
    onReachEnd,
    hasNextPage,
    isFetchingNextPage,
  ]);

  return {
    virtualizer,
    virtualItems,
    totalSize: virtualizer.getTotalSize(),
  };
}
